import { QetaPage } from '@drodil/backstage-plugin-qeta';
import { AccentuatePage } from '@internal/backstage-plugin-accentuate';
import { AiCodingReadinessPage } from '@internal/backstage-plugin-ai-coding-readiness';
import { OrganisationInsightsPage } from '@internal/backstage-plugin-engineering-insights';
import { GithubAppCreationPage } from '@internal/backstage-plugin-github-app-creation';
import {
  KaapPage,
  KaaPTenantExplorerPage,
} from '@internal/backstage-plugin-kaap';
import {
  AllScorecardsPage,
  OperationalExcellencePage,
} from '@internal/backstage-plugin-scorecard-common-react';
import { AllureReportPageComponent } from '@internal/plugin-allure-report-frontend';
import { ApiAuthPage } from '@internal/plugin-api-auth';
import {
  apiDocsPlugin,
  ApiExplorerPage,
} from '@internal/plugin-api-governance';
import {
  ServiceCatalogExplorerPage,
  SubscriptionCatalogExplorerPage,
  KubernetesCatalogExplorerPage,
  SupervisoryOrgExplorerPage,
  ProxyCatalogExplorerPage,
  ApplicationCatalogExplorerPage,
  UserCatalogExplorerPage,
  DaaSCatalogExplorerPage,
  AIToolsCatalogExplorerPage,
  RepositoryExplorerPage,
  JiraTeamsExplorerPage,
  PagerDutyTeamsExplorerPage,
} from '@internal/plugin-catalog-explorer';
import { PromptLibraryPage } from '@internal/plugin-prompt-library';
import {
  GenericValuePickerFieldExtension,
  GenericValueInputPickerFieldExtension,
  RepoNamePickerExtension,
  GithubTeamPickerFieldExtension,
  AzSecurityGroupPickerFieldExtension,
  AzureSubscriptionPickerFieldExtension,
  CatalogUserPickerFieldExtension,
  CatalogNamePickerFieldExtension,
  AzureLoginFieldExtension,
  UrlInputExtension,
  GithubLoginFieldExtension,
  MyCostCentersPickerFieldExtension,
  KaapTenantPickerFieldExtension,
  KaapTenantNameFieldExtension,
  KaapAzureTokenFieldExtension,
  KaapApplicationSelectorFieldExtension,
  KaapCabInformFieldExtension,
  KaapClusterNodeGroupPickerFieldExtension,
  KaapGitOpsConfigFieldExtension,
  AzureSecurityGroupMultiValueExtension,
  KaaPAzureSecurityGroupMultiValueExtension,
  TextInputFieldExtension,
  KubernetesClusterPickerFieldExtension,
  DaasComponentPickerFieldExtension,
  DaasProjectInputFieldExtension,
  DaasApplicationInputFieldExtension,
  DaasApplicationSetInputFieldExtension,
  TypographyLabelFieldExtension,
  UserGroupsPickerFieldExtension,
  SuporgPickerFieldExtension,
} from '@internal/plugin-scaffolder-field-react';
import { SecretsSharingFrontendPage } from '@internal/plugin-secrets-sharing-frontend';
import { TomtomLabPage } from '@internal/plugin-tomtom-lab';
import { tomtomTheme } from '@internal/plugin-tomtom-theme-react';
import { SelectFieldFromApiExtension } from '@roadiehq/plugin-scaffolder-frontend-module-http-request-field';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import React, { PropsWithChildren } from 'react';
import { Route, useLocation } from 'react-router';

import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { FeatureFlagged } from '@backstage/core-app-api';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import {
  microsoftAuthApiRef,
  IdentityApi,
  discoveryApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import {
  ReportIssue,
  ExpandableNavigation,
  TextSize,
  LightBox,
} from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import {
  UserSettingsPage,
  SettingsLayout,
} from '@backstage/plugin-user-settings';
import { UnifiedThemeProvider } from '@backstage/theme';

import { CssBaseline } from '@material-ui/core';

import { GrafanaLogo } from './GrafanaLogo';
import { OtelLogo } from './OtelLogo';
import { apis } from './apis';
import { Root } from './components/Root';
import { entityPage } from './components/catalog/EntityPage';
import { CliAuthPage } from './components/cliAuth';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';
import { EntityProvidersSettings } from './components/entityProviders/EntityProvidersSettings';
import { HelpPage } from './components/help/HelpPagePlugin';
import { ArgoCDLogoSmall } from './components/home/ArgoCDLogo';
import { HomePage } from './components/home/HomePage';
import { OrgGraphPage } from './components/org-graph/OrgGraphPage';
import { CustomReviewStep } from './components/scaffolder/CustomReviewStep';
import { searchPage } from './components/search/SearchPage';
import { setTokenCookie } from './cookieAuth';
import * as plugins from './plugins';

const app = createApp({
  apis,
  plugins: Object.values(plugins),
  icons: {
    grafana: GrafanaLogo,
    argocd: ArgoCDLogoSmall,
    otel: OtelLogo,
  },
  featureFlags: [
    {
      pluginId: '',
      name: 'show-api-keys-feature',
      description: 'Enables the API Keys tab under settings',
    },
    {
      pluginId: '',
      name: 'show-accentuate-sidebar',
      description: 'Enables the Accentuate sidebar item',
    },
    {
      pluginId: '',
      name: 'show-api-gateway-proxy-management',
      description: 'Enables the central gateway tab (proxy management)',
    },
    {
      pluginId: '',
      name: 'show-kaap-old',
      description: 'Enables the old KaaP UI',
    },
    {
      pluginId: '',
      name: 'show-reliability',
      description: 'Enables the Reliability tab in Engineering Insights',
    },
  ],
  components: {
    SignInPage: props => {
      const discoveryApi = useApi(discoveryApiRef);
      return (
        <SignInPage
          {...props}
          auto
          provider={{
            id: 'microsoft-auth-provider',
            title: 'Azure AD (TomTom Employees)',
            message: 'Sign in using Azure AD.',
            apiRef: microsoftAuthApiRef,
          }}
          onSignInSuccess={async (identityApi: IdentityApi) => {
            setTokenCookie(
              await discoveryApi.getBaseUrl('cookie'),
              identityApi,
            );

            props.onSignInSuccess(identityApi);
          }}
        />
      );
    },
  },
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  themes: [
    {
      id: 'tomtom-theme',
      title: 'TomTom Theme',
      variant: 'light',
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={tomtomTheme}>
          <CssBaseline>{children}</CssBaseline>
        </UnifiedThemeProvider>
      ),
    },
    // {
    //   id: 'community-dark',
    //   title: 'Community Dark',
    //   // @ts-ignore
    //   variant: 'community-dark',
    //   Provider: ({ children }) => (
    //     <UnifiedThemeProvider theme={themes.dark} children={children} />
    //   ),
    // },
  ],
});

const scaffolderPage = (
  <ScaffolderPage
    components={{
      ReviewStepComponent: CustomReviewStep,
    }}
    groups={[
      {
        title: 'API',
        filter: entity => entity?.metadata?.tags?.includes('api') ?? false,
      },
      {
        title: 'CI/CD',
        filter: entity => entity?.metadata?.tags?.includes('ci-cd') ?? false,
      },
      {
        title: 'Catalog',
        filter: entity => entity?.metadata?.tags?.includes('catalog') ?? false,
      },
      {
        title: 'Code',
        filter: entity =>
          entity?.metadata?.tags?.includes('scaffolding') ?? false,
      },
      {
        title: 'Infra',
        filter: entity =>
          (entity?.metadata?.tags?.includes('infra') &&
            !entity?.metadata?.tags?.includes('no-manual-use')) ??
          false,
      },
      {
        title: 'Incidents',
        filter: entity =>
          entity?.metadata?.tags?.includes('incidents') ?? false,
      },
      {
        title: 'Observability',
        filter: entity =>
          entity?.metadata?.tags?.includes('observability') ?? false,
      },
      {
        title: 'Ownership',
        filter: entity =>
          entity?.metadata?.tags?.includes('ownership') ?? false,
      },
      {
        title: 'Security',
        filter: entity => entity?.metadata?.tags?.includes('security') ?? false,
      },
      {
        title: 'Team',
        filter: entity => entity?.metadata?.tags?.includes('team') ?? false,
      },
      {
        title: 'No manual use',
        filter: entity =>
          entity?.metadata?.tags?.includes('no-manual-use') ?? false,
      },
    ]}
    templateFilter={entity =>
      !entity?.metadata?.tags?.includes('hidden-template')
    }
  />
);

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog-list" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <ExpandableNavigation />
        <TextSize />
        <LightBox />
        <Mermaid
          config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }}
        />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={scaffolderPage}>
      <ScaffolderFieldExtensions>
        <GenericValuePickerFieldExtension />
        <GenericValueInputPickerFieldExtension />
        <RepoNamePickerExtension />
        <GithubTeamPickerFieldExtension />
        <CatalogUserPickerFieldExtension />
        <CatalogNamePickerFieldExtension />
        <AzureLoginFieldExtension />
        <GithubLoginFieldExtension />
        <UrlInputExtension />
        <AzSecurityGroupPickerFieldExtension />
        <AzureSubscriptionPickerFieldExtension />
        <SelectFieldFromApiExtension />
        <MyCostCentersPickerFieldExtension />
        <KaapTenantPickerFieldExtension />
        <KaapTenantNameFieldExtension />
        <KaapAzureTokenFieldExtension />
        <KaapApplicationSelectorFieldExtension />
        <KaapCabInformFieldExtension />
        <KaapClusterNodeGroupPickerFieldExtension />
        <KaapGitOpsConfigFieldExtension />
        <AzureSecurityGroupMultiValueExtension />
        <KaaPAzureSecurityGroupMultiValueExtension />
        <TextInputFieldExtension />
        <KubernetesClusterPickerFieldExtension />
        <DaasComponentPickerFieldExtension />
        <DaasProjectInputFieldExtension />
        <DaasApplicationInputFieldExtension />
        <DaasApplicationSetInputFieldExtension />
        <TypographyLabelFieldExtension />
        <UserGroupsPickerFieldExtension />
        <SuporgPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <FeatureFlagged with="show-api-gateway-proxy-management">
      <Route path="/api-proxy" element={<ProxyCatalogExplorerPage />} />
    </FeatureFlagged>
    <Route
      path="/subscriptions"
      element={<SubscriptionCatalogExplorerPage />}
    />
    <Route path="/kubernetes" element={<KubernetesCatalogExplorerPage />} />
    <Route
      path="/supervisory-groups"
      element={<SupervisoryOrgExplorerPage />}
    />
    <Route path="/kaap" element={<KaaPTenantExplorerPage />} />
    <FeatureFlagged with="show-kaap-old">
      <Route path="/kaap-old" element={<KaapPage />} />
    </FeatureFlagged>
    <Route path="/daas" element={<DaaSCatalogExplorerPage />} />
    <Route path="/ai-tools" element={<AIToolsCatalogExplorerPage />} />
    <Route path="/applications" element={<ApplicationCatalogExplorerPage />} />
    <Route path="/repositories" element={<RepositoryExplorerPage />} />
    <Route path="/jira-teams" element={<JiraTeamsExplorerPage />} />
    <Route path="/pagerduty-teams" element={<PagerDutyTeamsExplorerPage />} />
    <Route
      path="/services"
      element={
        <ServiceCatalogExplorerPage
          title="Service"
          initialKindFilter="Component"
          initialTypeFilter="service"
        />
      }
    />
    <Route path="/users" element={<UserCatalogExplorerPage />} />
    <Route
      path="/tech-radar"
      element={
        <>
          <TechRadarPage
            title="TomTom Tech Radar"
            pageTitle=""
            subtitle=""
            height={750}
          />
          <div
            style={{
              padding: '48px 24px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              boxSizing: 'border-box',
              width: '100%',
              overflowX: 'hidden',
              borderTop: '1px solid #e0e0e0',
              backgroundColor: '#fff',
              marginTop: '0px',
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  marginBottom: '16px',
                }}
              >
                What is the Tech Radar?
              </h2>
              <p style={{ marginBottom: '16px', lineHeight: '1.6' }}>
                The TomTom Tech Radar is a list of technologies, complemented by
                an assessment result, called ring assignment. We use four rings
                with the following semantics:
              </p>
              <ul style={{ paddingLeft: '20px', lineHeight: '1.8' }}>
                <li style={{ marginBottom: '12px' }}>
                  <strong>ADOPT</strong> — Technologies we have high confidence
                  in to serve our purpose, also in large scale. Technologies
                  with a usage culture in our TomTom production environment, low
                  risk and recommended to be widely used.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>TRIAL</strong> — Technologies that we have seen work
                  with success in project work to solve a real problem; first
                  serious usage experience that confirm benefits and can uncover
                  limitations. TRIAL technologies are slightly more risky; some
                  engineers in our organization walked this path and will share
                  knowledge and experiences.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>ASSESS</strong> — Technologies that are promising and
                  have clear potential value-add for us; technologies worth to
                  invest some research and prototyping efforts in to see if it
                  has impact. ASSESS technologies have higher risks; they are
                  often brand new and highly unproven in our organisation. You
                  will find some engineers that have knowledge in the technology
                  and promote it, you may even find teams that have started a
                  prototyping effort.
                </li>
                <li style={{ marginBottom: '12px' }}>
                  <strong>HOLD</strong> — Technologies not recommended to be
                  used for new projects. Technologies that we think are not
                  (yet) worth to (further) invest in. HOLD technologies should
                  not be used for new projects, but usually can be continued for
                  existing projects.
                </li>
              </ul>
            </div>
            <div>
              <div style={{ marginBottom: '32px' }}>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    marginBottom: '16px',
                  }}
                >
                  What is the purpose?
                </h2>
                <p style={{ lineHeight: '1.6' }}>
                  The Tech Radar is a tool to inspire and support Engineering
                  teams at TomTom to pick the best technologies for new
                  projects; it provides a platform to share knowledge and
                  experience in technologies, to reflect on technology decisions
                  and continuously evolve our technology landscape. Based on the
                  pioneering work of ThoughtWorks, our Tech Radar sets out the
                  changes in technologies that are interesting in software
                  development — changes that we think our engineering teams
                  should pay attention to and use in their projects.
                </p>
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    marginBottom: '16px',
                  }}
                >
                  How do we maintain it?
                </h2>
                <p style={{ lineHeight: '1.6' }}>
                  The Tech Radar is maintained in a central GitHub JSON file.
                  Teams can propose updates via pull requests in the
                  <a
                    href="https://github.com/tomtom-internal/meta-data/blob/main/config/pe-capabilities.json"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2' }}
                  >
                    <span> capabilities file</span>
                  </a>
                </p>
              </div>
              <div style={{ marginTop: '32px' }}>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    marginBottom: '16px',
                  }}
                >
                  How does it connect to the Golden Path?
                </h2>
                <p style={{ lineHeight: '1.6' }}>
                  The Tech Radar and the{' '}
                  <a
                    href="https://frontstage.tomtomgroup.com/docs/default/component/pe-golden-path"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2' }}
                  >
                    Golden Path
                  </a>{' '}
                  are complementary — the Radar defines <em>which</em>{' '}
                  technologies are adopted across TomTom, while the Golden Path
                  provides the opinionated, ready-to-use templates and tooling
                  that put those adopted technologies into practice.
                </p>
              </div>
            </div>
          </div>
        </>
      }
    />

    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <FeatureFlagged with="show-api-keys-feature">
        <SettingsLayout.Route path="/api-keys" title="API Keys">
          <ApiAuthPage />
        </SettingsLayout.Route>
      </FeatureFlagged>
      <SettingsLayout.Route path="/entity-providers" title="Entity Providers">
        <EntityProvidersSettings />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/org-graph" element={<OrgGraphPage />} />
    <Route path="/ai-library" element={<PromptLibraryPage />} />
    <Route path="/ai-coding-readiness" element={<AiCodingReadinessPage />} />
    <Route path="/qeta" element={<QetaPage title="Questions" />} />
    <Route path="/help" element={<HelpPage />} />
    <Route path="/cli-auth" element={<CliAuthPage />} />
    <Route path="/tomtom-lab" element={<TomtomLabPage />} />
    <Route path="/devtools" element={<DevToolsPage />}>
      {customDevToolsPage}
    </Route>
    <Route
      path="/catalog-unprocessed-entities"
      element={<CatalogUnprocessedEntitiesPage />}
    />
    <Route path="/secrets-sharing" element={<SecretsSharingFrontendPage />} />
    <Route path="/github-app-creation" element={<GithubAppCreationPage />} />
    <Route path="/accentuate" element={<AccentuatePage />} />
    <Route path="/allure" element={<AllureReportPageComponent />} />
    <Route path="/scorecards" element={<OperationalExcellencePage />} />
    <Route path="/all-scorecards" element={<AllScorecardsPage />} />
    <Route
      path="/engineering-insights"
      element={<OrganisationInsightsPage />}
    />
    <Route path="/ai-coding-readiness" element={<AiCodingReadinessPage />} />
  </FlatRoutes>
);

export const FrontStageAppWrapper = ({ children }: PropsWithChildren<{}>) => {
  const location = useLocation();
  const CLASS_NAME = 'FrontStageCatalog';
  const pathnames = [
    '/catalog',
    'not:/catalog/default/',
    '/api-docs',
    '/docs',
    'not:/docs/default',
  ];

  const shouldAddFrontStageClassToPage = (pathname: string) => {
    const processedPathnames = pathnames
      .filter(e => pathname.includes(e.replace(/not\:/g, '')))
      .map(e =>
        e.includes('not:')
          ? !pathname.includes(e.replace(/not\:/g, ''))
          : pathname.includes(e),
      );

    if (processedPathnames.length === 0) return false;
    return processedPathnames.reduce((acc, current) => acc && current);
  };

  const pathname = location.pathname;

  return (
    <div className={shouldAddFrontStageClassToPage(pathname) ? CLASS_NAME : ''}>
      {children}
    </div>
  );
};

export default app.createRoot(
  <>
    <AlertDisplay transientTimeoutMs={2500} />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
